import "./services/sentry/init"; // Sentry Initialization as early as possible
import "./styles/app.scss";
import "./styles/index.css";
import "./styles/theme.css";

import {
    AuthProvider,
    getRefreshToken,
    getSessionToken,
    isSessionTokenExpired,
    refresh,
    useDescope,
    useSession,
    useUser,
} from "@descope/react-sdk";
import { CircularProgress } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { HelmetProvider } from "react-helmet-async";
import { Navigate, Route, BrowserRouter as Router, useLocation } from "react-router-dom";
import { dayMs, secondMs } from "verdi-common/timeConstants";
import * as VerdiWebClient from "verdi-web-client";
import { setProcessingFunction } from "verdi-web-client/Models/AMasterIndex";
import { queryClient } from "./api/queryClient";
import { ToastProvider } from "./components/ui/toast";
import { registerStreamCacheSessionCleanup } from "./features/data-viewer/stream-cache/register-stream-cache-session-cleanup";
import NavBar from "./features/navigation/nav-bar";
import AccountManager from "./pages/account-manager/account-manager";
import CustomGraphPage from "./pages/custom-graph-page/custom-graph-page";
import { DataExport } from "./pages/data-export/data-export";
import ErrorPageNotFound from "./pages/error-page-not-found/error-page-not-found";
import Login from "./pages/login/login";
import { MissionPage } from "./pages/mission/mission-page";
import { ModalProvider } from "./providers/modal-provider";
import { WallClockProvider } from "./providers/wall-clock";
import {
    identifyAuthenticatedUser,
    identifyCurrentOrganization,
    refreshAuthenticatedUser,
    resetAnalytics,
} from "./services/analytics/identity";
import { checkIsPartner, checkIsVerdiStaff } from "./services/analytics/staff-flags";
import { getDefaultPositionReverseGeocode } from "./services/geocoding/reverse-geocode";
import GloballyControlledLoadingSpinner from "./services/loading-spinner/globally-controlled-loading-spinner";
import FocusContext from "./services/map-management/focus-context";
import AreaofInterestMapEntity from "./services/map-management/map-entities/area-of-interest-map-entity";
import DeviceMapEntity from "./services/map-management/map-entities/device-map-entity";
import IrrigationSetMapEntity from "./services/map-management/map-entities/irrigation-set-map-entity";
import OverlayEntity from "./services/map-management/map-entities/overlay-entity";
import { ThirdPartyDeviceMapEntity } from "./services/map-management/map-entities/third-party-device-map-entity";
import ZoneMapEntity from "./services/map-management/map-entities/zone-map-entity";
import AllOnLoadMessages from "./services/message-handlers/all-on-load-messages";
import { SentryRoutes, setSentryUser } from "./services/sentry";
import { doInitialMapPan, doInitialScheduleLoad } from "./services/session-manager/on-account-load";
import { setGoogleAnalyticsTags } from "./services/session-manager/set-google-analytics-tags";
import { setAccountAndStartSessionLoad, setupVerdiWebClient } from "./services/session-manager/setup-verdi-web-client";
import { MUItheme } from "./styles/colour-palette";
import GlobalOptions from "./utils/global-options";
import { preventAccidentalPageZooming } from "./utils/prevent-accidental-page-zooming";
import { URLParams } from "./utils/url-params";

// Flag to check if listener is added
let isListenerAdded = false;

// Get the device memory
const deviceMemory = navigator.deviceMemory;

// Log some useful information
console.info(`Device memory (GB): ${navigator.deviceMemory}`);
console.info(`Cloud Build ID: ${import.meta.env.VITE_BUILD_ID}`);
console.info(`Cloud Build Commit SHA: ${import.meta.env.VITE_COMMIT_SHA}`);

// Set the initial date to load images to the current date
let dateToLoadImages = Date.now();

// Adjust the date to load NDVI images based on device memory and device type
if (deviceMemory) {
    if (deviceMemory >= 6 && !isMobile) {
        // If device memory is 6 or more and the device is not mobile, set the date to 30 days ago
        dateToLoadImages -= dayMs * 30;
    } else if (deviceMemory >= 4) {
        // If device memory is 4 or more, set the date to 15 days ago
        dateToLoadImages -= dayMs * 15;
    }
}

// Lazy load the Map page
const MapPage = React.lazy(
    () =>
        new Promise((resolve, reject) => {
            import("./pages/map/map")
                .then((result) => resolve(result.default ? result : { default: result }))
                .catch(reject);
        }),
);

// Lazy load the Settings page
const SettingsPage = React.lazy(
    () =>
        new Promise((resolve, reject) => {
            import("./pages/settings/settings")
                .then((result) => resolve(result.default ? result : { default: result }))
                .catch(reject);
        }),
);

// Lazy load the Debugging page
const DebuggerPage = React.lazy(
    () =>
        new Promise((resolve, reject) => {
            import("./pages/device-debugger/debugging-main-page")
                .then((result) => resolve(result.default ? result : { default: result }))
                .catch(reject);
        }),
);

// Set the font size of the root element to 16px for mobile devices
if (isMobile) {
    document.children[0].style.fontSize = "16px";
}

// Initialize an empty array to hold map entities
let mapEntities = [];

// Define a function to process different model types
setProcessingFunction((model) => {
    try {
        // Mapping of model categories to their respective Map Entity classes
        const categoryClassMap = {
            irrigationDevice: DeviceMapEntity,
            blockValve: DeviceMapEntity,
            SensoterraMoisture: DeviceMapEntity,
            zone: ZoneMapEntity,
            overlay: OverlayEntity,
            aoi: AreaofInterestMapEntity,
            irrigationSet: IrrigationSetMapEntity,
            thirdPartyDevice: ThirdPartyDeviceMapEntity,
        };

        // Get the class for the model category
        const EntityClass = categoryClassMap[model.category];

        // If the model category has a map entity, create a new instance of the corresponding Map Entity class and push it to the mapEntities array
        if (EntityClass) {
            // Handle special case for overlay entity
            if (model.category === "overlay") {
                mapEntities.push(new EntityClass(model, new Date(model.date).valueOf() > dateToLoadImages));

                // Handle all other model categories
            } else {
                mapEntities.push(new EntityClass(model, true));
            }
        }
    } catch (e) {
        console.warn("Error assigning VerdiClient entity to MapEntity", e);
    }

    // Clear the array for the next processing cycle
    mapEntities = [];
});

// makes sure that zooming only zooms the map and not the page.
preventAccidentalPageZooming();

// Log the contents of /humans.txt to the console
fetch("/humans.txt")
    .then((response) => response.text())
    .then((data) => {
        console.info(`Humans.txt:\n`, data);
    })
    .catch((error) => {
        console.warn("Error fetching the humans.txt file:", error);
    });

function App() {
    if (import.meta.env.VITE_ENVIRONMENT) {
        // Set the favicon based on the VITE_ENVIRONMENT environment variable.
        const iconTypes = ["apple-touch-icon", "favicon-32x32", "favicon-16x16"];
        iconTypes.forEach((iconType) => {
            const iconLink = document.getElementById(iconType);
            if (iconLink) {
                iconLink.href = `./favicon/${import.meta.env.VITE_ENVIRONMENT}/${iconType}.png`;
            }
        });
    } else {
        console.warn(
            "VITE_ENVIRONMENT is not set in the environment variables. Environment variables: ",
            import.meta.env,
        );
    }

    // State for tracking position
    const [position, setPosition] = useState(undefined);

    if (!isListenerAdded) {
        // If the listener is not added, add it
        console.debug(`Registering session handler event listeners...`);

        setupVerdiWebClient();
        registerStreamCacheSessionCleanup();

        // TODO(jeff): Until we move SessionHandler/`doFetch` into a React context, this IoC approach is sufficient.
        //             See: <https://linear.app/verdiag/issue/SWD-2334/consider-transitioning-our-sessionhandler-to-a-react-provider-style>
        VerdiWebClient.SessionHandler.sessionMethods = {
            getRefreshToken,
            getSessionToken,
            isSessionTokenExpired,
            refresh,
        };

        // Add a listener to handle login events in verdi-web-client's SessionHandler
        VerdiWebClient.SessionHandler.onLogIn.addListener((e) => {
            localStorage.setItem("userData", JSON.stringify(e));
            setGoogleAnalyticsTags();

            // Identify the Descope user; org is grouped later on session load.
            const descopeUser = e?.descopeUser;
            if (descopeUser?.userId) {
                identifyAuthenticatedUser({
                    userId: descopeUser.userId,
                    name: descopeUser.name,
                    email: descopeUser.email,
                    roleNames: descopeUser.roleNames,
                    isAdmin: Boolean(e?.admin ?? VerdiWebClient.SessionHandler.admin),
                    adminUsername: e?.adminUsername,
                });
            }

            setAccountAndStartSessionLoad();

            // TODO(jeff): Evaluate where position updates are still necessary; centralize as best as possible.
            setPosition(VerdiWebClient.SessionHandler.defaultPosition);
        });

        VerdiWebClient.SessionHandler.onSessionLoad.addListener(async () => {
            FocusContext.updateLoadHandler("Processing Data", "sessionLoad");
            console.debug("Loading user session...");

            // Sentry - add user info to the Sentry SDK
            setSentryUser();

            // Group the current organization (re-runs on account switch).
            const currentOrg = VerdiWebClient.SessionHandler.currentUserObject;
            if (currentOrg?.id) {
                identifyCurrentOrganization({
                    id: currentOrg.id,
                    name: currentOrg.name,
                    adminUseOnly: currentOrg.adminUseOnly,
                });
            }

            // Lookup the default position reverse geocode (async, non-blocking)
            try {
                getDefaultPositionReverseGeocode({ forceRefresh: true });
            } catch (e) {
                console.error("Error getting default position reverse geocode", e);
            }

            // honestly, I have no idea what this does anymore. Something to do with google analytics
            if (VerdiWebClient.SessionHandler.admin) {
                window.dataLayer.push({
                    sessionLoadedAdmin: 1,
                });
            } else {
                // eslint-disable-next-line no-undef
                gtag("event", "page_view", {
                    page_title: "Dashboard",
                    client_id: VerdiWebClient.SessionHandler.admin ? "ADMIN" : VerdiWebClient.SessionHandler.curUser,
                    user_agent: window.navigator.userAgent,
                });
            }

            console.debug("Updating account URL parameter...");
            try {
                if (VerdiWebClient.SessionHandler.admin) {
                    URLParams.setParam("account", VerdiWebClient.SessionHandler.currentUserObject.dbID);
                } else {
                    URLParams.unsetParam("account");
                }
            } catch (e) {
                console.warn("Failed to update account URL parameter:", e);
            }

            // wait for async tasks to complete
            await Promise.race([Promise.allSettled([doInitialMapPan(), doInitialScheduleLoad()]), 10 * secondMs]);
            FocusContext.resolveLoadHandler("sessionLoad");
        });

        let storedData = {};
        try {
            storedData = JSON.parse(localStorage.getItem("userData"));
        } catch (e) {
            console.warn(e);
        }

        // TODO(jeff): Move event handlers down to Main for AuthProvider context
        if (storedData) {
            if (storedData.defaultPos) {
                VerdiWebClient.SessionHandler.defaultPosition = storedData.defaultPos;
            }
        }

        // Add the d and m classes to the root element to make style rules for mobile and desktop work properly
        const rootElement = document.getElementById("root");
        if (rootElement) {
            if (isMobile) {
                rootElement.classList.add("m");
                rootElement.style.setProperty("--nav-bar-height", "45px");
                document.body.style.setProperty("--nav-bar-height", "45px");
            } else {
                rootElement.classList.add("d");
            }

            if (GlobalOptions.iconSize) {
                rootElement.classList.add(`iconSize--${GlobalOptions.iconSize}`);
            }
        }

        isListenerAdded = true;
    }

    return (
        <HelmetProvider>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={MUItheme}>
                    <QueryClientProvider client={queryClient}>
                        {/*
                         * Toasts sit bottom-left (the component's default), where notistack's snackbars
                         * already are, clear of the device card in the bottom-right slot and of the right-hand
                         * save bars, so a toast can stay long enough to read a sentence.
                         */}
                        <ToastProvider timeout={5 * secondMs}>
                            <SnackbarProvider autoHideDuration={7 * secondMs} maxSnack={3}>
                                <ModalProvider>
                                    <WallClockProvider>
                                        <AuthProvider
                                            projectId={import.meta.env.VITE_DESCOPE_PROJECT_ID}
                                            autoRefresh
                                            persistTokens
                                            storeLastAuthenticatedUser
                                        >
                                            <Router>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <Suspense fallback={<CircularProgress />}>
                                                        <Main position={position} />
                                                    </Suspense>
                                                </LocalizationProvider>
                                            </Router>
                                        </AuthProvider>
                                    </WallClockProvider>
                                </ModalProvider>
                            </SnackbarProvider>
                        </ToastProvider>
                    </QueryClientProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </HelmetProvider>
    );
}

function Main({ position }) {
    const { isAuthenticated, isSessionLoading } = useSession();
    const { user, isUserLoading } = useUser();
    const { logout } = useDescope();

    const handleLogOut = useCallback(() => {
        VerdiWebClient.SessionHandler.logOut();
    }, []);

    // Handle all logout side-effects: clean up local storage and sign out of Descope.
    // Notably: any time SessionHandler.logOut() is called internally as a result of
    // a failed `WhoAmI` request or any request where a session cannot be refreshed,
    // the user-related local storage should be cleared and Descope's session should
    // be signed out to trigger an automatic redirect to the login page.
    useEffect(() => {
        const onLogOut = async () => {
            localStorage.removeItem("userData");
            // Unlink future events from the logged-out user's session.
            resetAnalytics();
            await logout();
        };
        VerdiWebClient.SessionHandler.onLogOut.addListener(onLogOut);
        return () => {
            VerdiWebClient.SessionHandler.onLogOut.removeListener(onLogOut);
        };
    }, [logout]);

    useEffect(() => {
        async function awaitLogIn() {
            if (isAuthenticated && !isSessionLoading && !isUserLoading) {
                await VerdiWebClient.SessionHandler.logIn(user);
            }
        }

        awaitLogIn();
    }, [isAuthenticated, isSessionLoading, isUserLoading, user]);

    // Refresh the PostHog person profile so it is always up to date.
    useEffect(() => {
        if (isSessionLoading || isUserLoading || !user || !isAuthenticated) {
            return;
        }

        const roleNames = user.roleNames ?? [];
        const isAdmin = VerdiWebClient.SessionHandler.admin;
        refreshAuthenticatedUser({ name: user.name, email: user.email, roleNames, isAdmin });
    }, [isAuthenticated, isSessionLoading, isUserLoading, user]);

    useEffect(() => {
        if (isSessionLoading || isUserLoading) {
            return undefined;
        }

        if (!window.Frill) {
            return undefined;
        }

        let frillCall;
        if (user && isAuthenticated) {
            const email = (user.email ?? "").toLowerCase();
            const roleNames = user.roleNames ?? [];
            const isAdmin = VerdiWebClient.SessionHandler.admin;
            // Frill is identified independently, but shares the same staff-flag primitives.
            const isVerdiStaff = checkIsVerdiStaff({ email, roleNames, isAdmin });
            const isPartner = checkIsPartner(roleNames);
            const companies =
                user.userTenants?.map((v) => ({
                    id: v.tenantId,
                    name: v.tenantName,
                })) ?? [];
            // eslint-disable-next-line new-cap
            frillCall = window.Frill("identify", {
                email: email,
                name: user.name,
                attributes: {
                    isVerdiStaff: isVerdiStaff,
                    isPartner: isPartner,
                },
                companies: companies,
            });
        } else {
            // eslint-disable-next-line new-cap
            frillCall = window.Frill("unidentify");
        }

        return () => {
            if (frillCall?.destroy) {
                frillCall.destroy();
            }
        };
    }, [isAuthenticated, isSessionLoading, isUserLoading, user]);

    return (
        <>
            <NavBar />
            <AllOnLoadMessages />
            <GloballyControlledLoadingSpinner />
            <RoutesComponent position={position} logOutHandler={handleLogOut} />
        </>
    );
}

function LogOutRedirect({ logOutHandler }) {
    useEffect(() => {
        logOutHandler();
    }, [logOutHandler]);
    const { search, hash } = window.location;
    return <Navigate replace to={`/auth/login${search}${hash}`} />;
}

function RequireAuthentication({ adminOnly, withoutSuspense, children }) {
    const location = useLocation();
    const { isAuthenticated } = useSession();
    const { user, isUserLoading } = useUser();

    // This is duplicative of what SessionHandler.admin provides, but useSession/useUser are in sync with AuthProvider context
    // For route-related decision making, avoid using SessionHandler as it may not be initialized yet
    const isAdmin = user?.roleNames?.includes("Verdi Administrator") || false;

    if (!isAuthenticated) {
        return (
            <Navigate
                to={`/auth/login${location.search}${location.hash}`}
                replace
                state={{ path: location.pathname }}
            />
        );
    }

    if (isUserLoading) {
        return <CircularProgress />;
    }

    if (adminOnly && !isAdmin) {
        return <Navigate to={`/${location.search}${location.hash}`} />;
    }

    return withoutSuspense ? children : <React.Suspense fallback={"Loading..."}>{children}</React.Suspense>;
}

// If the user is logged in, it shows the main application routes.
function RoutesComponent({ position, logOutHandler }) {
    const routes = (
        <>
            <Route path={"/auth/logout"} element={<LogOutRedirect logOutHandler={logOutHandler} />} />
            <Route path={"/auth/*"} element={<Login />} />

            <Route
                path={"/"}
                element={
                    <RequireAuthentication>
                        <MapPage defaultPosition={VerdiWebClient.SessionHandler.defaultPosition} />
                    </RequireAuthentication>
                }
            />
            <Route
                path={"/mission"}
                element={
                    <RequireAuthentication>
                        <MissionPage />
                    </RequireAuthentication>
                }
            />
            <Route
                path={"/settings"}
                element={
                    <RequireAuthentication>
                        <SettingsPage defaultPosition={position} />
                    </RequireAuthentication>
                }
            />
            {/* These are the routes that only admins will be able to acccess */}
            <Route
                path={"/DeviceDebugger"}
                element={
                    <RequireAuthentication adminOnly>
                        <DebuggerPage />
                    </RequireAuthentication>
                }
            />
            <Route
                path={"/AccountManager"}
                element={
                    <RequireAuthentication adminOnly>
                        <AccountManager />
                    </RequireAuthentication>
                }
            />
            {/* Permanent redirect for legacy /UserManager path */}
            <Route path={"/UserManager"} element={<Navigate to={"/AccountManager"} replace />} />

            {/* TODO: Delete this route and component in 2026, we should have the new graphs done by then */}
            {/* This is a hack feature to create ad hoc graphs for certain customers */}
            <Route
                path={"/customGraph"}
                element={
                    <RequireAuthentication>
                        <CustomGraphPage />
                    </RequireAuthentication>
                }
            />
            <Route
                path={"/export"}
                element={
                    <RequireAuthentication>
                        <DataExport />
                    </RequireAuthentication>
                }
            />

            {/* Add error handling so that if they go to another route other than the ones listed above then we will either redirect them or take them back to the login */}
            <Route path={"*"} element={<ErrorPageNotFound />} />
        </>
    );

    return <SentryRoutes>{routes}</SentryRoutes>;
}

export default App;
